import { useMemo, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { OwnerInput } from "@/lib/validations/owner";
import type { City, DocumentType } from "@/hooks/useLookups";
import { cn } from "@/lib/utils";
import {
  convertDisplayToISO,
  formatISOToDisplay,
  getDateErrorMessage,
  maskDateInput,
} from "@/lib/date-utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMaskedDigitsCaret } from "@/hooks/useMaskedDigitsCaret";
import {
  applyDocumentMask,
  getDocumentMaxLength,
  getDocumentPlaceholder,
  validateDocument,
} from "@/lib/utils/document-masks";

import { CustomIcons } from "@/components/icons/CustomIcons";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Button, Input, Select } from "@akuna/ui";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@akuna/ui";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { EyeOff, Plus } from "lucide-react";

type LookupItem = { id: string; name: string };
type StateItem = { id: string; name: string; code?: string | null };

export type OwnerFormSectionProps = {
  form: UseFormReturn<OwnerInput>;

  genders?: LookupItem[];
  documentTypes?: DocumentType[];
  leadSources?: LookupItem[];
  states?: StateItem[];

  birthDateInput: string;
  setBirthDateInput: (value: string) => void;
  isBirthDateCalendarOpen: boolean;
  setIsBirthDateCalendarOpen: (open: boolean) => void;

  documentInput: string;
  setDocumentInput: (value: string) => void;

  phones: OwnerInput["phones"];
  addresses?: OwnerInput["addresses"];

  addressStates: (string | null)[];
  setAddressStates: (updater: (prev: (string | null)[]) => (string | null)[]) => void;
  addressCities: Record<number, City[]>;
  addressCepLoading: Record<number, boolean>;
  cityNameCache: Record<string, string>;
  setCityNameCache: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  cityDisplayValues: Record<number, string>;
  setCityDisplayValues: (updater: (prev: Record<number, string>) => Record<number, string>) => void;

  fetchCitiesForState: (stateId: string, index: number, cityNameToSelect?: string) => Promise<void>;
  handleAddAddress: () => void;
  handleRemoveAddress: (index: number) => void;
  handleCepLookup: (index: number) => Promise<void>;
};

const maskPhoneInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);

  if (digits.length <= 10) {
    const part1 = digits.slice(0, 2);
    const part2 = digits.slice(2, 6);
    const part3 = digits.slice(6, 10);

    return [part1 && `(${part1}`, part1 && part1.length === 2 ? ") " : "", part2, part3 && `-${part3}`]
      .join("")
      .trim();
  }

  const part1 = digits.slice(0, 2);
  const part2 = digits.slice(2, 7);
  const part3 = digits.slice(7, 11);

  return [part1 && `(${part1}`, part1 && part1.length === 2 ? ") " : "", part2, part3 && `-${part3}`]
    .join("")
    .trim();
};

const maskCepInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export function OwnerFormSection(props: OwnerFormSectionProps) {
  const { t } = useTranslation("patients");
  const {
    form,
    genders,
    documentTypes,
    leadSources,
    states,
    birthDateInput,
    setBirthDateInput,
    isBirthDateCalendarOpen,
    setIsBirthDateCalendarOpen,
    documentInput,
    setDocumentInput,
    phones,
    addresses,
    addressStates,
    setAddressStates,
    addressCities,
    addressCepLoading,
    cityNameCache,
    setCityNameCache,
    cityDisplayValues,
    setCityDisplayValues,
    fetchCitiesForState,
    handleAddAddress,
    handleRemoveAddress,
    handleCepLookup,
  } = props;

  const { inputRef: birthDateInputRef, captureCaret: captureBirthDateCaret } = useMaskedDigitsCaret(birthDateInput);
  const isMobile = useIsMobile();
  const nativeBirthDateInputRef = useRef<HTMLInputElement>(null);

  const documentTypeId = form.watch("document_type_id");
  const selectedDocumentType = useMemo(
    () => documentTypes?.find((type) => type.id === documentTypeId) as DocumentType | undefined,
    [documentTypes, documentTypeId],
  );
  const documentPlaceholder = getDocumentPlaceholder(selectedDocumentType);
  const documentMaxLength = getDocumentMaxLength(selectedDocumentType);

  return (
    <div className="space-y-8">
      {/* ===== Dados do responsável ===== */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("ownerForm.fields.name")} <span className="text-destructive ">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder={t("ownerForm.placeholders.ownerName")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 ">
            <FormField
              control={form.control}
              name="document_type_id"
              render={({ field, fieldState }) => (
                <FormItem className="min-w-[130px]">
                  <Select
                    label={t("ownerForm.fields.documentType")}
                    placeholder={t("ownerForm.placeholders.select")}
                    options={(documentTypes ?? []).map((type) => ({ value: type.id, label: type.name }))}
                    value={field.value ?? ""}
                    onValueChange={(value) => {
                      field.onChange(value);
                      const docType = documentTypes?.find((t) => t.id === value) as DocumentType | undefined;
                      const masked = applyDocumentMask(documentInput, docType);
                      setDocumentInput(masked);
                      form.clearErrors("document");
                      form.setValue("document", masked, { shouldDirty: true });
                    }}
                  />
                  {fieldState.error ? (
                    <p className="text-sm font-medium text-destructive">{fieldState.error.message}</p>
                  ) : null}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="document"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ownerForm.fields.document")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={documentPlaceholder}
                      maxLength={documentMaxLength}
                      value={documentInput}
                      onBlur={() => {
                        const validation = validateDocument(documentInput, selectedDocumentType);
                        if (!validation.isValid) {
                          form.setError("document", { message: validation.error });
                        } else {
                          form.clearErrors("document");
                        }
                      }}
                      onChange={(event) => {
                        const masked = applyDocumentMask(event.target.value, selectedDocumentType);
                        setDocumentInput(masked);
                        field.onChange(masked);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="birth_date"
            render={({ field }) => {
              const dateError = getDateErrorMessage(birthDateInput);
              return (
                <FormItem>
                  <FormLabel>{t("ownerForm.fields.birthDate")}</FormLabel>
                  <FormControl>
                    {isMobile ? (
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder={t("ownerForm.placeholders.birthDate")}
                          inputMode="numeric"
                          ref={birthDateInputRef}
                          value={birthDateInput}
                          className="!flex-1"
                          onChange={(event) => {
                            const rawValue = event.target.value;
                            captureBirthDateCaret(rawValue, event.target.selectionStart);

                            const maskedValue = maskDateInput(rawValue);
                            setBirthDateInput(maskedValue);
                            const iso = convertDisplayToISO(maskedValue);
                            field.onChange(iso);
                          }}
                          aria-invalid={Boolean(dateError)}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="!w-10 !h-10"
                          aria-label={t("ownerForm.actions.selectBirthDate")}
                          onClick={() => {
                            const input = nativeBirthDateInputRef.current;
                            if (!input) return;
                            if (typeof input.showPicker === "function") {
                              input.showPicker();
                            } else {
                              input.click();
                            }
                          }}
                        >
                          <CustomIcons name="calendar" className="!h-4 !w-4 !text-color-dark" />
                        </Button>
                        <input
                          ref={nativeBirthDateInputRef}
                          type="date"
                          className="sr-only"
                          tabIndex={-1}
                          max={new Date().toISOString().slice(0, 10)}
                          value={field.value ?? ""}
                          onChange={(event) => {
                            const iso = event.target.value;
                            if (!iso) return;
                            field.onChange(iso);
                            setBirthDateInput(formatISOToDisplay(iso));
                          }}
                        />
                      </div>
                    ) : (
                      <Popover open={isBirthDateCalendarOpen} onOpenChange={setIsBirthDateCalendarOpen}>
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder={t("ownerForm.placeholders.birthDate")}
                            inputMode="numeric"
                            ref={birthDateInputRef}
                            value={birthDateInput}
                            className="!flex-1"
                            onChange={(event) => {
                              const rawValue = event.target.value;
                              captureBirthDateCaret(rawValue, event.target.selectionStart);

                              const maskedValue = maskDateInput(rawValue);
                              setBirthDateInput(maskedValue);
                              const iso = convertDisplayToISO(maskedValue);
                              field.onChange(iso);
                            }}
                            aria-invalid={Boolean(dateError)}
                          />
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="!w-10 !h-10"
                              aria-label={t("ownerForm.actions.selectBirthDate")}
                            >
                              <CustomIcons name="calendar" className="!h-4 !w-4 !text-color-dark" />
                            </Button>
                          </PopoverTrigger>
                        </div>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={
                              field.value
                                ? (() => {
                                    const [year, month, day] = field.value.split("-");
                                    if (!year || !month || !day) return undefined;
                                    return new Date(Number(year), Number(month) - 1, Number(day));
                                  })()
                                : undefined
                            }
                            disabled={(date) => date > new Date()}
                            onSelect={(date) => {
                              if (!date) return;
                              const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
                              field.onChange(iso);
                              setBirthDateInput(formatISOToDisplay(iso));
                              setIsBirthDateCalendarOpen(false);
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </FormControl>
                  {dateError ? <p className="text-sm text-destructive">{dateError}</p> : <FormMessage />}
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="gender_id"
            render={({ field, fieldState }) => (
              <FormItem>
                <Select
                  label={t("ownerForm.fields.gender")}
                  placeholder={t("ownerForm.placeholders.select")}
                  options={(genders ?? []).map((gender) => ({ value: gender.id, label: gender.name }))}
                  value={field.value ?? ""}
                  onValueChange={field.onChange}
                />
                {fieldState.error ? (
                  <p className="text-sm font-medium text-destructive">{fieldState.error.message}</p>
                ) : null}
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="profession"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("ownerForm.fields.profession")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("ownerForm.placeholders.profession")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lead_source_id"
            render={({ field, fieldState }) => (
              <FormItem>
                <Select
                  label={t("ownerForm.fields.leadSource")}
                  placeholder={t("ownerForm.placeholders.select")}
                  options={(leadSources ?? []).map((source) => ({ value: source.id, label: source.name }))}
                  value={field.value ?? ""}
                  onValueChange={field.onChange}
                />
                {fieldState.error ? (
                  <p className="text-sm font-medium text-destructive">{fieldState.error.message}</p>
                ) : null}
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="emails.0.email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("ownerForm.fields.email")}</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={t("ownerForm.placeholders.email")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="marketing_consent"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center gap-2 rounded-md border border-[color:var(--border-default)] px-4 py-1">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div className="leading-none !my-1">
                  <p className="text-sm font-medium text-color-medium !mt-0">{t("ownerForm.fields.marketingConsent")}</p>
                  <p className="text-sm text-color-medium">{t("ownerForm.fields.marketingHelp")}</p>
                </div>
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* ===== Contatos ===== */}
      <div className="space-y-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-md font-semibold text-color-dark">{t("ownerForm.sections.phones")}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                form.setValue(
                  "phones",
                  [...(phones ?? []), { phone_number: "", is_whatsapp: true, is_primary: false }],
                  { shouldDirty: true },
                )
              }
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("ownerForm.actions.addPhone")}
            </Button>
          </div>

          {(phones ?? []).map((_, index) => (
            <div key={`phone-${index}`} className="grid grid-cols-1 md:grid-cols-3 gap-4 rounded-lg border p-4">
              <FormField
                control={form.control}
                name={`phones.${index}.phone_number`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ownerForm.fields.phoneNumber")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("ownerForm.placeholders.phone")}
                        inputMode="tel"
                        value={field.value ?? ""}
                        onChange={(event) => field.onChange(maskPhoneInput(event.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex items-center justify-between gap-4">
                <FormField
                  control={form.control}
                  name={`phones.${index}.is_whatsapp`}
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-center gap-2 md:mt-8">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="font-normal !mt-0">{t("ownerForm.fields.whatsApp")}</FormLabel>
                    </FormItem>
                  )}
                />
              </div>

              {index > 0 && (
                <div className="order-first md:order-last mb-[-38px] md:mb-0 flex items-start justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    className="hover:!text-destructive items-end"
                    onClick={() =>
                      form.setValue(
                        "phones",
                        (phones ?? []).filter((_, i) => i !== index),
                        { shouldDirty: true },
                      )
                    }
                    aria-label={t("ownerForm.actions.removePhone")}
                  >
                    <CustomIcons name="trash" className="!h-5 !w-5 text-inherit" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ===== Endereços ===== */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-md font-semibold text-color-dark">{t("ownerForm.sections.addresses")}</p>
          <Button type="button" variant="outline" size="sm" onClick={handleAddAddress}>
            <Plus className="h-4 w-4" />
            {t("ownerForm.actions.addAddress")}
          </Button>
        </div>

        {(addresses ?? []).map((_, index) => {
          const citiesForState = addressCities[index] ?? [];
          const cityId = form.watch(`addresses.${index}.city_id`);
          const selectedCity = citiesForState.find((city) => city.id === (cityId || ""));
          const displayCityName = selectedCity?.name ?? (cityId ? cityNameCache[cityId] : undefined);

          return (
            <div key={`address-${index}`} className="space-y-4 rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{t("ownerForm.fields.address", { index: index + 1 })}</p>
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(
                    "h-9 w-9 hover:!text-destructive",
                    (addresses ?? []).length === 1 && index === 0 && "invisible pointer-events-none ",
                  )}
                  onClick={() => handleRemoveAddress(index)}
                  aria-label={t("ownerForm.actions.removeAddress")}
                >
                  <CustomIcons name="trash" className="!h-5 !w-5 text-inherit" />
                </Button>
              </div>

              <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name={`addresses.${index}.zip_code`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("ownerForm.fields.zipCode")}</FormLabel>
                      <FormControl>
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder={t("ownerForm.placeholders.zipCode")}
                            {...field}
                            value={field.value || ""}
                            onChange={(event) => {
                              const masked = maskCepInput(event.target.value);
                              field.onChange(masked);
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            disabled={addressCepLoading[index]}
                            onClick={() => void handleCepLookup(index)}
                          >
                            {addressCepLoading[index] ? t("ownerForm.actions.searchingZipCode") : t("ownerForm.actions.searchZipCode")}
                          </Button>
                        </div>
                      </FormControl>
                      {addressCepLoading[index] ? (
                        <p className="text-xs text-muted-foreground">{t("ownerForm.zipCodeHelp")}</p>
                      ) : null}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`addresses.${index}.street`}
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Logradouro</FormLabel>
                      <FormControl>
                        <Input placeholder="Rua / Avenida" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name={`addresses.${index}.number`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número</FormLabel>
                      <FormControl>
                        <Input placeholder="Número" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`addresses.${index}.complement`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Complemento</FormLabel>
                      <FormControl>
                        <Input placeholder="Complemento" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`addresses.${index}.neighborhood`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bairro</FormLabel>
                      <FormControl>
                        <Input placeholder="Bairro" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Estado"
                  placeholder={t("ownerForm.placeholders.select")}
                  options={(states ?? []).map((s) => ({ value: s.id, label: s.name }))}
                  value={addressStates[index] ?? ""}
                  onValueChange={(value) => {
                    setAddressStates((prev) => {
                      const next = [...prev];
                      next[index] = value;
                      return next;
                    });
                    form.setValue(`addresses.${index}.city_id`, "", { shouldDirty: true });
                    void fetchCitiesForState(value, index);
                  }}
                />

                <FormField
                  control={form.control}
                  name={`addresses.${index}.city_id`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cidade</FormLabel>
                      <FormControl>
                        <Autocomplete
                          options={citiesForState.map((city) => ({
                            label: city.name,
                            value: city.id,
                          }))}
                          value={cityDisplayValues[index] ?? displayCityName ?? ""}
                          onChange={(value) => {
                            setCityDisplayValues((prev) => ({ ...prev, [index]: value }));

                            const matchedCity = citiesForState.find(
                              (city) => city.name.toLowerCase() === value.toLowerCase(),
                            );

                            if (matchedCity) {
                              field.onChange(matchedCity.id);
                              setCityNameCache((prev) => ({ ...prev, [matchedCity.id]: matchedCity.name }));
                              form.clearErrors(`addresses.${index}.city_id`);
                            } else {
                              field.onChange("");
                              if (value.trim()) {
                                form.setError(`addresses.${index}.city_id`, {
                                  type: "manual",
                                  message: "Selecione uma cidade da lista",
                                });
                              } else {
                                form.clearErrors(`addresses.${index}.city_id`);
                              }
                            }
                          }}
                          onSelect={(option) => {
                            field.onChange(option.value);
                            setCityDisplayValues((prev) => ({ ...prev, [index]: option.label }));
                            setCityNameCache((prev) => ({ ...prev, [option.value]: option.label }));
                            form.clearErrors(`addresses.${index}.city_id`);
                          }}
                          placeholder="Digite o nome da cidade..."
                          emptyMessage="Nenhuma cidade encontrada."
                          disabled={!addressStates[index]}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
